"use client";

import { useLocale } from "next-intl";
import { useTransition } from "react";

import { usePathname, useRouter } from "@/i18n/navigation";
import { routing, type AppLocale } from "@/i18n/routing";

type Props = {
  ariaLabel: string;
};

export function LocaleSwitcher({ ariaLabel }: Props) {
  const locale = useLocale() as AppLocale;
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();

  function switchTo(next: AppLocale) {
    if (next === locale) return;
    startTransition(() => {
      router.replace(pathname, { locale: next });
    });
  }

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      aria-busy={isPending || undefined}
      className="ml-2 inline-flex items-center gap-[2px] rounded-full border border-[var(--border)] p-1 font-mono text-[11px]"
      style={{ letterSpacing: "0.06em" }}
    >
      {routing.locales.map((code) => {
        const active = code === locale;
        return (
          <button
            key={code}
            type="button"
            onClick={() => switchTo(code)}
            aria-pressed={active}
            className={
              "rounded-full border-0 bg-transparent px-[10px] py-1 transition-colors " +
              (active
                ? "bg-brand-electric text-white"
                : "text-fg3 hover:text-fg1")
            }
            style={
              active
                ? {
                    background: "var(--accent)",
                    color: "#fff",
                    boxShadow: "0 0 14px rgba(30,63,232,.55)",
                  }
                : undefined
            }
          >
            {code.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}
